import { getPlatforms } from '@ionic/core/components';
import { setupIonicReact } from '@ionic/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';

import App from '@/app/App';
import '@/app/styles/index.css';
import { StartupLoader } from '@/components/loading/StartupLoader';
import { resolveAppDensityMode } from '@/features/shared/lib/appDensityMode';
import { resolveAppExperienceMode } from '@/features/shared/lib/appExperienceMode';

const updateSW = registerSW({ immediate: true });
setupIonicReact();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

const root = ReactDOM.createRoot(rootElement);

const applyDisplayModeClass = () => {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
  const platforms = getPlatforms(window);
  const htmlElement = document.documentElement;
  const mode = resolveAppExperienceMode(window);
  const densityMode = resolveAppDensityMode(window);

  htmlElement.classList.toggle('app-standalone', isStandalone);
  htmlElement.classList.toggle('app-platform-ios', platforms.includes('ios'));
  htmlElement.classList.toggle('app-platform-android', platforms.includes('android'));
  htmlElement.classList.toggle('app-platform-mobile', platforms.includes('mobile'));
  htmlElement.classList.toggle('app-platform-tablet', platforms.includes('tablet'));
  htmlElement.classList.toggle('app-experience-mobile-pwa', mode === 'mobile-pwa');
  htmlElement.classList.toggle('app-experience-mobile-browser', mode === 'mobile-browser');
  htmlElement.classList.toggle('app-experience-desktop-web', mode === 'desktop-web');
  htmlElement.classList.toggle('app-density-compact', densityMode === 'compact');
  htmlElement.classList.toggle('app-density-cozy', densityMode === 'cozy');
  htmlElement.classList.toggle('app-density-comfortable', densityMode === 'comfortable');
};

applyDisplayModeClass();

window.matchMedia('(display-mode: standalone)').addEventListener('change', applyDisplayModeClass);
window.matchMedia('(max-width: 767px)').addEventListener('change', applyDisplayModeClass);
window
  .matchMedia('(max-width: 1023px), (pointer: coarse)')
  .addEventListener('change', applyDisplayModeClass);
window.matchMedia('(pointer: coarse)').addEventListener('change', applyDisplayModeClass);

const waitForControllerChange = (timeoutMs = 5000) =>
  new Promise<void>((resolve) => {
    const timer = window.setTimeout(() => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      resolve();
    }, timeoutMs);

    const onControllerChange = () => {
      window.clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      resolve();
    };

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
  });

const registerReloadOnControllerChange = () => {
  let reloading = false;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) {
      return;
    }

    reloading = true;
    window.location.reload();
  });
};

const renderApp = () => {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
};

const bootstrap = async () => {
  try {
    root.render(
      <React.StrictMode>
        <StartupLoader />
      </React.StrictMode>
    );

    if ('serviceWorker' in navigator) {
      registerReloadOnControllerChange();
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration) {
        await registration.update();

        if (registration.waiting) {
          root.render(
            <React.StrictMode>
              <StartupLoader
                title="Updating AarogyaFlow"
                subtitle="Installing the latest version"
              />
            </React.StrictMode>
          );
          const controllerChange = waitForControllerChange();
          await updateSW();
          await controllerChange;
        }
      }
    }
  } catch {
    // Fall through and render the app if SW update handling fails.
  }

  renderApp();
};

void bootstrap();
